/**
 * Utility functions for handling scroll behavior with fixed header offset
 */

// Height of the fixed header/navbar in pixels
// Navbar h-16 = 64px, plus some extra padding for better UX
const HEADER_OFFSET = 80;

/**
 * Scrolls to an element with proper offset to account for fixed header
 * @param {HTMLElement|string} elementOrId - Element or element ID to scroll to
 * @param {Object} options - Scroll options
 * @param {number} options.offset - Additional offset in pixels (default: HEADER_OFFSET)
 * @param {string} options.behavior - Scroll behavior: 'smooth' or 'instant' (default: 'smooth')
 * @param {boolean} options.highlight - Whether to add highlight effect (default: false)
 */
export const scrollToElement = (elementOrId, options = {}) => {
  const {
    offset = HEADER_OFFSET,
    behavior = 'smooth',
    highlight = false
  } = options;

  // Get the element
  const element = typeof elementOrId === 'string' 
    ? document.querySelector(elementOrId) || document.getElementById(elementOrId)
    : elementOrId;

  if (!element) {
    console.warn(`Element not found: ${elementOrId}`);
    return;
  }

  // Calculate the target scroll position
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;

  // Scroll to the calculated position
  window.scrollTo({
    top: offsetPosition,
    behavior: behavior
  });

  // Add highlight effect if requested
  if (highlight) {
    element.style.boxShadow = '0 0 20px rgba(199, 50, 21, 0.3)';
    setTimeout(() => {
      element.style.boxShadow = '';
    }, 3000);
  }
};

/**
 * Scrolls to a product anchor from URL hash
 * Waits for content to load before scrolling
 * @param {string} hash - URL hash (e.g., '#product-123')
 * @param {number} delay - Delay in ms before scrolling (default: 300)
 */
export const scrollToProductAnchor = (hash, delay = 300) => {
  if (!hash || !hash.startsWith('#product-')) {
    return;
  }

  // Wait for the page to render, then scroll
  setTimeout(() => {
    scrollToElement(hash, {
      offset: HEADER_OFFSET,
      behavior: 'smooth',
      highlight: true
    });
  }, delay);
};

/**
 * Scrolls to top of page
 * @param {string} behavior - Scroll behavior: 'smooth' or 'instant' (default: 'instant')
 */
export const scrollToTop = (behavior = 'instant') => {
  window.scrollTo({
    top: 0,
    behavior: behavior
  });
};

